import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { format } from 'date-fns';
import api from '@/axios';
import {
  GET_PURCHASE_ORDER_ACTIVITY_KEY,
  GET_PURCHASE_ORDER_KEY,
  UPDATE_PURCHASE_ORDER_DATES_KEY,
} from '@/keys';

export type UpdatePurchaseOrderDatesParams = {
  id: string;
  productionEndDate: Date | undefined;
  expeditionDate: Date | undefined;
  estimatedArrivalDate: Date | undefined;
  deliveryDate: Date | undefined;
};

function toIsoDate(date: Date | undefined): string | null {
  if (!date) {
    return null;
  }
  return format(date, 'yyyy-MM-dd');
}

const updatePurchaseOrderDates = async ({
  id,
  productionEndDate,
  expeditionDate,
  estimatedArrivalDate,
  deliveryDate,
}: UpdatePurchaseOrderDatesParams): Promise<void> => {
  await api.patch(`/purchase-orders/${id}`, {
    productionEndDate: toIsoDate(productionEndDate),
    expeditionDate: toIsoDate(expeditionDate),
    estimatedArrivalDate: toIsoDate(estimatedArrivalDate),
    deliveryDate: toIsoDate(deliveryDate),
  });
};

export const useUpdatePurchaseOrderDates = (
  purchaseOrderId: string,
): UseMutationResult<void, Error, UpdatePurchaseOrderDatesParams> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...UPDATE_PURCHASE_ORDER_DATES_KEY, purchaseOrderId],
    scope: { id: `purchase-order-dates-${purchaseOrderId}` },
    mutationFn: updatePurchaseOrderDates,
    onSuccess: async (_data, { id }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [...GET_PURCHASE_ORDER_KEY, id],
        }),
        queryClient.invalidateQueries({
          queryKey: [...GET_PURCHASE_ORDER_ACTIVITY_KEY, id],
        }),
      ]);
    },
  });
};
